import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Row, Col, Pagination } from 'react-bootstrap';
import AddJobCardModule from './AddJobCardModule';
import UpdateJobCardModule from './UpdateJobCardModule';
import ViewJobCardModule from './ViewJobCardModule';
import CollapseHeader from '../../../../core/common/collapse-header';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../router/all_routes';
import { debounce } from 'lodash';

/* ----------------------------- Navigation Items ----------------------------- */
const navItems = [
  { label: 'All Job Cards', icon: 'ti ti-clipboard', link: all_routes.jobCards, permission: 'ROLE_MANAGE_JOB_CARD' },
  { label: 'Maintenance Tasks', icon: 'ti ti-tool', link: all_routes.maintenanceTasks, permission: 'ROLE_ADD_TASK' },
];

/* ----------------------------- DTOs & Interfaces ----------------------------- */
interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
  email?: string;
}

interface ApiJobCardDTO {
  id: number;
  maintenanceTaskId: number | null;
  maintenanceDescription: string | null;
  parentJobCardId: number | null;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  comment: string | null;
  floorName?: string | null;
}

type StatusBase = 'PENDING' | 'IN_PROGRESS' | 'NOT_COMPLETED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
type StatusChip = 'ALL' | StatusBase;

/* ----------------------------- Helpers ----------------------------- */
const shortName = (label: string, takeLast: number = 3): string => {
  if (!label) return '—';
  const namePart = label.includes(' - ') ? (label.split(' - ').pop() || label) : label;
  const parts = namePart.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= takeLast) return namePart.trim();
  return parts.slice(-takeLast).join(' ');
};

const todayInColombo = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Colombo' }).format(new Date());

const getEffectiveStatus = (jc: ApiJobCardDTO): StatusBase => {
  const raw = (jc.status || '').toUpperCase();
  const s = (['PENDING','IN_PROGRESS','NOT_COMPLETED','COMPLETED','CANCELLED'].includes(raw)
    ? (raw as Exclude<StatusBase,'EXPIRED'>)
    : 'PENDING');
  if (s === 'COMPLETED' || s === 'CANCELLED') return s;
  const end = jc.scheduledEndDateTime ? new Date(jc.scheduledEndDateTime).getTime() : NaN;
  if (!Number.isNaN(end) && end < Date.now()) return 'EXPIRED';
  return s;
};

const statusChipColor = (status: StatusChip) => {
  switch (status) {
    case 'PENDING': return { on: 'bg-warning text-white', off: 'bg-warning-transparent text-warning', icon: 'ti-hourglass' };
    case 'IN_PROGRESS': return { on: 'bg-info text-white', off: 'bg-info-transparent text-info', icon: 'ti-player-play' };
    case 'NOT_COMPLETED': return { on: 'bg-danger text-white', off: 'bg-danger-transparent text-danger', icon: 'ti-alert-triangle' };
    case 'COMPLETED': return { on: 'bg-success text-white', off: 'bg-success-transparent text-success', icon: 'ti-check' };
    case 'CANCELLED': return { on: 'bg-dark text-white', off: 'bg-dark-transparent text-dark', icon: 'ti-square-x' };
    case 'EXPIRED': return { on: 'bg-secondary text-white', off: 'bg-secondary-transparent text-secondary', icon: 'ti-clock' };
    case 'ALL':
    default: return { on: 'bg-primary text-white', off: 'bg-primary-transparent text-primary', icon: 'ti-notebook' };
  }
};

const statusBadgeClasses = (status: StatusChip | string) => {
  const s = (status || '').toString().toUpperCase();
  if (s === 'PENDING') return 'bg-warning-transparent text-warning border';
  if (s === 'IN_PROGRESS') return 'bg-info-transparent text-info border';
  if (s === 'NOT_COMPLETED') return 'bg-danger-transparent text-danger border';
  if (s === 'COMPLETED') return 'bg-success-transparent text-success border';
  if (s === 'CANCELLED') return 'bg-dark-transparent text-dark border';
  if (s === 'EXPIRED') return 'bg-secondary-transparent text-secondary border';
  return 'bg-light text-dark border';
};

/* ----------------------------- Component ----------------------------- */
const JobCard: React.FC = () => {
  const [jobCards, setJobCards] = useState<ApiJobCardDTO[]>([]);
  const [selectedJobCard, setSelectedJobCard] = useState<ApiJobCardDTO | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(todayInColombo());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [statusChip, setStatusChip] = useState<StatusChip>('ALL');
  const navigate = useNavigate();

  /* ----------------------------- Responsive columns ----------------------------- */
  const useResponsiveCols = () => {
    const [cols, setCols] = React.useState<number>(() => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
      if (w >= 1200) return 3;
      if (w >= 768) return 2;
      return 1;
    });
    React.useEffect(() => {
      const onResize = () => {
        const w = window.innerWidth;
        if (w >= 1200) setCols(3);
        else if (w >= 768) setCols(2);
        else setCols(1);
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, []);
    return cols;
  };
  const cols = useResponsiveCols();

  /* ----------------------------- Style ----------------------------- */
  const C = {
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as React.CSSProperties,
    topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' } as React.CSSProperties,
    grid: (n: number) => ({ display: 'grid', gap: 16, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }) as React.CSSProperties,
    card: {
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      boxShadow: '0 6px 18px rgba(16,24,40,.06)',
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      height: '100%',
      transition: 'box-shadow .2s ease, transform .2s ease'
    } as React.CSSProperties,
    hover: { boxShadow: '0 12px 24px rgba(16,24,40,.12)', transform: 'translateY(-1px)' } as React.CSSProperties,
    title: { fontSize: '1rem', fontWeight: 800, color: '#0F172A', margin: 0 } as React.CSSProperties,
    pillRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' } as React.CSSProperties,
    pill: {
      display: 'inline-flex', gap: 6, alignItems: 'center',
      padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700
    } as React.CSSProperties,
    line: { display: 'flex', gap: 8, alignItems: 'center', color: '#475569', fontSize: 14 } as React.CSSProperties,
    peopleList: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' } as React.CSSProperties,
    peopleItem: { display: 'flex', gap: 6, alignItems: 'center' } as React.CSSProperties,
    icon: { width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties,
    hr: { margin: '12px 0', border: 0, borderTop: '1px solid #E5E7EB' } as React.CSSProperties,
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 } as React.CSSProperties,
    idText: { fontSize: 12, color: '#64748B' } as React.CSSProperties,
    actions: { display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
    aBtn: {
      width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    } as React.CSSProperties,
  };

  /* ----------------------------- Helpers ----------------------------- */
  const checkToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return true;
  }, [navigate]);

  /* ----------------------------- Fetchers ----------------------------- */
  const fetchJobCards = useCallback(async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    try {
      const payload: any = {
        date: selectedDate || ''
      };
      const response = await apiDefinitions.getAllJobCards(currentPage, payload);
      if (response?.data?.status === 'S1000') {
        const jobCardsData: ApiJobCardDTO[] = response.data.data || [];
        setJobCards(jobCardsData);
        const total = response.data.total || jobCardsData.length || 0;
        setTotalPages(Math.max(1, Math.ceil(total / 10)));
        setTotalCount(total);
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch job cards');
        setJobCards([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error fetching job cards');
      }
      setJobCards([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [checkToken, currentPage, selectedDate, navigate]);

  const debouncedFetch = useCallback(debounce(fetchJobCards, 400), [fetchJobCards]);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  /* ----------------------------- Handlers ----------------------------- */
  const handleOpenUpdate = useCallback(async (id: number) => {
    if (!checkToken()) return;
    try {
      const response = await apiDefinitions.getJobCardById(id);
      if (response?.data?.status === 'S1000') {
        const jobCard = response.data.data as ApiJobCardDTO;
        setSelectedJobCard(jobCard);
        setShowUpdateModal(true);
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch job card');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error fetching job card');
      }
    }
  }, [checkToken, navigate]);

  const handleOpenView = useCallback(async (id: number) => {
    if (!checkToken()) return;
    try {
      const response = await apiDefinitions.getJobCardById(id);
      if (response?.data?.status === 'S1000') {
        const jobCard = response.data.data as ApiJobCardDTO;
        setSelectedJobCard(jobCard);
        setShowViewModal(true);
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch job card');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error fetching job card');
      }
    }
  }, [checkToken, navigate]);

  const handlePageChange = (n: number) => setCurrentPage(n);

  /* ----------------------------- KPI & Chip counts ----------------------------- */
  const chipCounts = useMemo(() => {
    const counts: Record<StatusBase, number> = {
      PENDING: 0, IN_PROGRESS: 0, NOT_COMPLETED: 0, COMPLETED: 0, CANCELLED: 0, EXPIRED: 0,
    };
    jobCards.forEach((jc) => {
      const eff = getEffectiveStatus(jc);
      counts[eff] += 1;
    });
    const total = jobCards.length;
    return { ALL: total, ...counts } as Record<StatusChip, number>;
  }, [jobCards]);

  const visibleJobCards = useMemo(() => {
    if (statusChip === 'ALL') return jobCards;
    return jobCards.filter((jc) => getEffectiveStatus(jc) === (statusChip as StatusBase));
  }, [jobCards, statusChip]);

  /* ----------------------------- Card Item ----------------------------- */
  const JobCardItem: React.FC<{ jc: ApiJobCardDTO }> = ({ jc }) => {
    const [hover, setHover] = useState(false);
    const effStatus = getEffectiveStatus(jc);

    const people = [
      ...jc.responsiblePersons.map(p => ({
        label: p.empNumber ? `${p.empNumber} - ${p.name}` : p.name,
        icon: 'ti-user-check',
        color: 'info'
      })),
      ...jc.accountablePersons.map(p => ({
        label: p.empNumber ? `${p.empNumber} - ${p.name}` : p.name,
        icon: 'ti-user-shield',
        color: 'dark'
      }))
    ];

    return (
      <div
        style={{ ...C.card, ...(hover ? C.hover : {}) }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <h5 style={C.title}>
          {jc.maintenanceDescription || 'Maintenance Job'}
        </h5>

        <div style={C.pillRow}>
          <span className={`${statusBadgeClasses(effStatus)} fw-bold`} style={C.pill as any} title={effStatus}>
            <i className="ti ti-alert-circle" /> {effStatus.replace('_', ' ')}
          </span>
          {jc.parentJobCardId && (
            <span className="bg-info-transparent text-info" style={C.pill as any}>
              <i className="ti ti-git-branch" /> Child of #{jc.parentJobCardId}
            </span>
          )}
        </div>

        {jc.maintenanceTaskId && (
          <div style={C.line}>
            <span className="bg-primary-transparent text-primary" style={C.icon as any}>
              <i className="ti ti-tool" />
            </span>
            <span>Task ID: {jc.maintenanceTaskId}</span>
          </div>
        )}

        <div style={C.line}>
          <span className="bg-success-transparent text-success" style={C.icon as any}>
            <i className="ti ti-calendar" />
          </span>
          <span title={`${jc.scheduledDateTime || '-'}${jc.scheduledEndDateTime ? ' → ' + jc.scheduledEndDateTime : ''}`}>
            {jc.scheduledDateTime ? new Date(jc.scheduledDateTime).toLocaleString() : '-'}
            {jc.scheduledEndDateTime ? ` → ${new Date(jc.scheduledEndDateTime).toLocaleString()}` : ''}
          </span>
        </div>

        {/* People List - Multiple Responsible & Accountable */}
        {people.length > 0 && (
          <div style={C.line}>
            <span className="bg-purple-transparent text-purple" style={C.icon as any}>
              <i className="ti ti-users" />
            </span>
            <div style={C.peopleList}>
              {people.map((p: any, idx: number) => (
                <div key={idx} style={C.peopleItem} title={p.label}>
                  <span className={`bg-${p.color}-transparent text-${p.color}`} style={C.icon as any}>
                    <i className={`ti ${p.icon}`} />
                  </span>
                  <span>{shortName(p.label, 3)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <hr style={C.hr} />

        <div style={C.footer}>
          <span style={C.idText}>ID: {jc.id}</span>
          <div style={C.actions}>
            <button className="bg-info text-white" style={C.aBtn as any} title="View" onClick={() => handleOpenView(jc.id)}>
              <i className="ti ti-eye" />
            </button>
            {checkPermission('ROLE_MANAGE_JOB_CARD') && (
              <button className="bg-success text-white" style={C.aBtn as any} title="Edit" onClick={() => handleOpenUpdate(jc.id)}>
                <i className="ti ti-edit" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ----------------------------- Render ----------------------------- */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="row">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-8">
                <h4 className="page-title">Facility Maintenance / Job Card / All Job Cards</h4>
              </div>
              <div className="col-4 text-end">
                <div className="head-icons"><CollapseHeader /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div className="card-body pb-0 pt-2">
            <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
              {navItems.map((item, index) =>
                item.permission && checkPermission(item.permission) ? (
                  <li className="nav-item me-item" key={index}>
                    <Link to={item.link} className={`nav-link px-0 ${index === 0 ? 'active' : ''}`}>
                      <i className={item.icon}></i> {item.label}
                    </Link>
                  </li>
                ) : null
              )}
            </ul>
          </div>
        </div>

        {/* Filters + New */}
        <div className="card">
          <div className="card-body">
            <Row className="g-2 align-items-end">
              <Col xs="auto">
                <Form.Group className="mb-0">
                  <Form.Label className="mb-1">Date</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value || '')}
                      style={{ minWidth: 200 }}
                    />
                    <Button variant="outline-secondary" onClick={() => setSelectedDate('')} title="Clear date filter">
                      Clear
                    </Button>
                    <Button variant="outline-primary" onClick={() => setSelectedDate(todayInColombo())} title="Jump to today">
                      Today
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col xs="auto" className="ms-auto">
                {checkPermission('ROLE_MANAGE_JOB_CARD') && (
                  <Button variant="success" onClick={() => setShowAddModal(true)}>
                    + New Job Card
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </div>

        {/* Status Chips */}
        <div className="card mt-2">
          <div className="card-body d-flex flex-wrap gap-2 align-items-center">
            {(['ALL','PENDING','IN_PROGRESS','NOT_COMPLETED','COMPLETED','CANCELLED','EXPIRED'] as StatusChip[]).map((s) => {
              const meta = statusChipColor(s);
              const active = statusChip === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusChip(s)}
                  className={`d-inline-flex align-items-center gap-2 border-0 px-3 py-2 rounded-pill fw-bold shadow-sm ${active ? meta.on : meta.off}`}
                  style={{ fontSize: 12 }}
                >
                  <i className={`ti ${meta.icon}`} />
                  <span>{s.replace('_', ' ')}</span>
                  <span className="badge rounded-pill bg-light text-dark ms-1">{chipCounts[s]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cards Grid */}
        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div className="text-center my-4">
                <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
              </div>
            ) : visibleJobCards.length === 0 ? (
              <div className="alert alert-info mb-0">No job cards found.</div>
            ) : (
              <>
                <div style={C.grid(cols)}>
                  {visibleJobCards.map((jc) => (
                    <JobCardItem key={jc.id} jc={jc} />
                  ))}
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <Pagination>
                    {Array.from({ length: totalPages }, (_, idx) => (
                      <Pagination.Item
                        key={idx + 1}
                        active={idx + 1 === currentPage}
                        onClick={() => handlePageChange(idx + 1)}
                      >
                        {idx + 1}
                      </Pagination.Item>
                    ))}
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddJobCardModule
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          fetchJobCards();
        }}
      />

      {selectedJobCard && (
        <UpdateJobCardModule
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          onSuccess={() => {
            setShowUpdateModal(false);
            fetchJobCards();
          }}
          jobCard={selectedJobCard}
        />
      )}

      {selectedJobCard && (
        <ViewJobCardModule
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          jobCard={selectedJobCard}
        />
      )}
    </div>
  );
};

export default JobCard;